import type { JSX } from "react";
import type { Block, BlockType, Citation, ResolutionStepState, SuggestionAction } from "@shared/types";

/**
 * Block renderer registry (OpenSpec answer-rendering-lanes.md, Feature 2).
 *
 * The host renders answer blocks by looking up a renderer by `block.type` rather
 * than switching on the union, so a new block type adds a renderer here instead
 * of editing MessageBubble. This is the in-app stand-in for the module-registered
 * renderers the spec envisions; the registration seam is kept so a real module
 * boundary can be extracted later without touching call sites.
 */

/** Props every block renderer receives. */
export interface BlockRendererProps {
  block: Block;
  /** Send a follow-up message as the user (e.g. a branch "didn't help"). */
  onSendPrompt: (text: string) => void;
  /** Persist a resolution step-state change through the backend. */
  onMutateStep: (blockId: string, stepId: string, state: ResolutionStepState) => void;
  /** The turn's citations, so a markdown block can strip 【来源N】 markers that
   *  point at hidden sources (the visible chips render at the turn level). */
  citations: Citation[];
  /** The conversation this turn belongs to, for renderers that persist
   *  user actions against it (e.g. a next-steps dismissal). Absent in
   *  contexts without a session (previews). */
  conversationId?: string;
  /** Dispatch a non-prompt suggestion action (P4c): `upload` opens 我的资料库,
   *  `connect` opens the Feishu OAuth flow. Absent → those chips are hidden. */
  onSuggestionAction?: (action: SuggestionAction) => void;
  /** Open a generated artifact in the side canvas (FEATURE_ARTIFACTS · #7 · 7a).
   *  Absent → the artifact card renders read-only with no open affordance. */
  onOpenArtifact?: (artifactId: string) => void;
}

export type BlockRenderer = (props: BlockRendererProps) => JSX.Element;

const renderers = new Map<BlockType, BlockRenderer>();

/** Register the renderer for a block type. Last registration wins. */
export function registerBlockRenderer(type: BlockType, renderer: BlockRenderer): void {
  renderers.set(type, renderer);
}

/** The renderer for a block type, or undefined when none is registered. */
export function getBlockRenderer(type: BlockType): BlockRenderer | undefined {
  return renderers.get(type);
}
